import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { SET_NB_MATCH_REVIEW } from './matchReview.queries';
import {
  approvalAccount,
  candidateBlock,
  evictAfterDecision,
  candidateName,
  checkReview,
  describeReviewError,
  isSeveralAccounts,
  MatchItem,
  NOTE_MAX,
  noteRequired,
  ReviewState,
  signupName,
} from './matchReview';

interface ReviewDialogProps {
  item: MatchItem;
  review: ReviewState;
  onClose: (changed: boolean) => void;
}

const TITLES: Record<ReviewState, string> = {
  APPROVED: 'Same person',
  REJECTED: 'Not the same person',
  PENDING: 'Reopen',
};

/**
 * One decision on one row: approve (with the account, when several fit, and
 * the note a name-and-town match needs), reject, or reopen. The checks are the
 * API's own, made before sending; what it still refuses is shown in the dialog.
 */
export const ReviewDialog = ({ item, review, onClose }: ReviewDialogProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const several = isSeveralAccounts(item);
  const choosable = item.candidates.filter((c) => !candidateBlock(c));
  const [chosen, setChosen] = useState<number | null>(several && choosable.length === 1 ? choosable[0].userId : null);
  const [note, setNote] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const [save, { loading }] = useMutation(SET_NB_MATCH_REVIEW, {
    // Every tab, its counts and the sidebar's count move with a decision; and since card 28 the
    // contacts too: two become one, or one becomes two again.
    update: (cache) => {
      evictAfterDecision(cache);
    },
  });

  const name = signupName(item.signup, item.nbSignupId);
  const account = item.candidates.find((c) => c.userId === approvalAccount(item, chosen));
  const problem = review === 'PENDING' ? null : checkReview(item, review, note, chosen);
  const needsNote = noteRequired(item, review);

  const send = async () => {
    if (problem) return;
    setRefusal(null);
    try {
      await save({
        variables: {
          nbSignupId: item.nbSignupId,
          review,
          ...(review === 'APPROVED' && several ? { userId: chosen } : {}),
          ...(review !== 'PENDING' && note.trim() ? { note: note.trim() } : {}),
        },
      });
      onClose(true);
    } catch (error) {
      setRefusal(describeReviewError(error as Error));
    }
  };

  return (
    <Dialog open onClose={() => onClose(false)} fullWidth maxWidth="sm" fullScreen={phone} aria-labelledby="crm-review-title">
      <DialogTitle id="crm-review-title">
        {TITLES[review]}: {name}
      </DialogTitle>
      <DialogContent dividers>
        {review === 'APPROVED' && (
          <>
            {several ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Which account is this NationBuilder record?
                </Typography>
                <RadioGroup
                  aria-label="The account"
                  value={chosen != null ? String(chosen) : ''}
                  onChange={(event) => setChosen(Number(event.target.value))}
                >
                  {item.candidates.map((c) => {
                    const block = candidateBlock(c);
                    return (
                      <FormControlLabel
                        key={c.userId}
                        value={String(c.userId)}
                        disabled={Boolean(block)}
                        control={<Radio size="small" />}
                        label={
                          <span>
                            {candidateName(c)} (#{c.userId}){c.email ? `, ${c.email}` : ''}
                            {block ? ` – ${block}` : ''}
                          </span>
                        }
                      />
                    );
                  })}
                </RadioGroup>
              </>
            ) : (
              account && (
                <Typography variant="body2">
                  NationBuilder #{item.nbSignupId} is app account #{account.userId}, {candidateName(account)}.
                </Typography>
              )
            )}
            <Alert severity="info" sx={{ mt: 1.5 }}>
              This records the decision, with your name, and joins the two contacts into one: the member&apos;s. Their name,
              email and number stay as the app has them; the NationBuilder record&apos;s other addresses, numbers, tags and
              lists move to them, and its consent comes too, with an opt-out on either side kept. The record&apos;s history
              shows on the member&apos;s page. Reopening the match separates them again.
            </Alert>
          </>
        )}
        {review === 'REJECTED' && (
          <Typography variant="body2">
            {several
              ? 'None of these accounts is this NationBuilder record. It stays a NationBuilder contact with no app account.'
              : 'The NationBuilder record and the app account are different people. Both stay as they are, unlinked.'}
          </Typography>
        )}
        {review === 'PENDING' && (
          <Typography variant="body2">
            The record goes back to Waiting for someone to decide again.
            {item.reviewNote ? ' Its note is cleared.' : ''}
            {item.merge
              ? ' The two contacts are separated again: what the merge moved goes back to the NationBuilder record, unless it has changed since.'
              : ''}
          </Typography>
        )}

        {review !== 'PENDING' && (
          <TextField
            label={needsNote ? 'What agrees besides the name and the town?' : 'Note (optional)'}
            required={needsNote}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            multiline
            minRows={3}
            fullWidth
            sx={{ mt: 2 }}
            inputProps={{ maxLength: NOTE_MAX + 100 }}
            helperText={
              needsNote
                ? 'Name and town alone often mean a namesake. Say what else matches: a street, a number stored without its country code, a call.'
                : `${note.trim().length.toLocaleString('en-US')} of ${NOTE_MAX.toLocaleString('en-US')} characters`
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) send();
            }}
          />
        )}

        {refusal && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {refusal}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1, pl: 1 }}>
          {problem && note.length > 0 && (
            <Typography variant="caption" color="error.main">
              {problem}
            </Typography>
          )}
        </Box>
        <Button onClick={() => onClose(false)} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={review === 'REJECTED' ? 'inherit' : 'primary'}
          onClick={send}
          disabled={loading || Boolean(problem)}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ boxShadow: 'none' }}
        >
          {review === 'APPROVED' ? 'Approve' : review === 'REJECTED' ? 'Reject' : 'Reopen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
